import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';
import {
  saveRequest,
  updateSpreads,
  setCoverImage,
  setPhotobookCover,
  setPhotobookCoverText
} from 'modules/ScrPhotobook/store/slices/photobook';
import {
  addImageToThePage,
  addNewSpread,
  deleteImage,
  deleteSpread,
  fetchPhotobook,
  fetchPhotobooks,
  replaceImage,
  swapImage,
  updateCover,
  updateCoverFonts,
  updateCoverPosition,
  updateCoverTitles,
  updateImageBgPosition,
  updatePhotobookLayout
} from 'modules/ScrPhotobook/store/slices/photobook/actions';
import { syncCartPhotobooksToLocalStorage } from 'modules/ScrPhotobook/helpers/photobookCartStorage';
import { RootState } from 'old-store/store';

const capturePreviousStateMiddleware = createListenerMiddleware();
let previousState: any = null;

capturePreviousStateMiddleware.startListening({
  matcher: isAnyOf(
    addNewSpread.pending,
    deleteSpread.pending,
    addImageToThePage.pending,
    replaceImage.pending,
    updateImageBgPosition.pending,
    updatePhotobookLayout.pending,
    deleteImage.pending,
    swapImage.pending,
    updateCover.pending,
    updateCoverPosition.pending,
    updateCoverTitles.pending,
    updateCoverFonts.pending
  ),
  effect: (_action, { getState }) => {
    // Previous state before the action is fulfilled
    const state = getState() as RootState;
    previousState = state.photobook;
  }
});

const requestSavingMiddleware = createListenerMiddleware();

requestSavingMiddleware.startListening({
  matcher: isAnyOf(
    addNewSpread.fulfilled,
    deleteSpread.fulfilled,
    addImageToThePage.fulfilled,
    replaceImage.fulfilled,
    updateImageBgPosition.fulfilled,
    updatePhotobookLayout.fulfilled,
    deleteImage.fulfilled,
    swapImage.fulfilled,
    updateCover.fulfilled,
    updateCoverPosition.fulfilled,
    updateCoverTitles.fulfilled,
    updateCoverFonts.fulfilled
  ),
  effect: (action, { dispatch }) => {
    // Fulfilled action with its payload and previous state
    dispatch(
      saveRequest({
        action: action.type,
        payload: previousState
      })
    );
  }
});

// Middleware to sync cart photobooks to localStorage for cross-tab validation
const cartPhotobookSyncMiddleware = createListenerMiddleware();

cartPhotobookSyncMiddleware.startListening({
  matcher: isAnyOf(
    // Async thunk fulfilled actions
    fetchPhotobooks.fulfilled,
    fetchPhotobook.fulfilled,
    addNewSpread.fulfilled,
    deleteSpread.fulfilled,
    addImageToThePage.fulfilled,
    replaceImage.fulfilled,
    updateImageBgPosition.fulfilled,
    updatePhotobookLayout.fulfilled,
    deleteImage.fulfilled,
    swapImage.fulfilled,
    updateCover.fulfilled,
    updateCoverPosition.fulfilled,
    updateCoverTitles.fulfilled,
    updateCoverFonts.fulfilled,
    // Sync reducer actions (for immediate localStorage update)
    updateSpreads,
    setCoverImage,
    setPhotobookCover,
    setPhotobookCoverText
  ),
  effect: (_action, { getState }) => {
    const state = getState() as RootState;
    const photobooks = state.photobook?.photobooks || [];

    // Sync all photobooks to localStorage (filter by cart IDs when validating)
    if (photobooks.length > 0) {
      // Sync all photobooks - pass all IDs
      const allPhotobookIds = photobooks.map((pb) => pb._id);
      syncCartPhotobooksToLocalStorage(photobooks, allPhotobookIds);
    }
  }
});

export const combinedPhotobookMiddlewares = [
  capturePreviousStateMiddleware.middleware,
  requestSavingMiddleware.middleware,
  cartPhotobookSyncMiddleware.middleware
];
